import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import axiosInstance from '../../api/axios';
import { AlertCircle, Activity, Users, TrendingUp, RefreshCw, Shield, History } from 'lucide-react';
import { resolveIsAdmin } from '../../utils/adminAccess';

const AdminDashboard = () => {
  const { user, userProfile } = useAuthContext();
  const [systemHealth, setSystemHealth] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Check if user is admin
  const isAdmin = resolveIsAdmin({ user, userProfile, operatorClaim: null });

  useEffect(() => {
    if (isAdmin) {
      fetchDashboardData();
    }
  }, [isAdmin]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch system health
      const healthResponse = await axiosInstance.get('/get_system_health');
      setSystemHealth(healthResponse.data.health);
      
      // Fetch usage statistics
      const statsResponse = await axiosInstance.get('/get_usage_stats?period=24h');
      setUsageStats(statsResponse.data.stats);
      
      // Fetch recent logs
      const logsResponse = await axiosInstance.get('/get_system_logs?limit=10');
      setRecentLogs(logsResponse.data.logs || []);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num || 0);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  if (!isAdmin) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-300 rounded-lg p-6">
          <div className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-red-600" />
            <p className="text-red-800">Access Denied: Admin privileges required</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !refreshing) {
    return (
      <div className="p-8">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-400" />
          <p className="text-gray-300">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6 bg-[#0b0d2a] min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-white">Admin Dashboard</h1>
        <button 
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Link
          to="/admin"
          className="flex items-center justify-between gap-4 bg-cyan-950/40 border border-cyan-700/50 rounded-lg p-4 hover:bg-cyan-950/60 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Activity className="h-5 w-5 text-cyan-300" />
            <div>
              <div className="font-semibold text-white">Quantum Admin</div>
              <div className="text-sm text-gray-400">Subscribers, tickers, users, trade logs</div>
            </div>
          </div>
          <span className="text-cyan-300 text-sm">Open →</span>
        </Link>
        <Link
          to="/admin/client-trade-history"
          className="flex items-center justify-between gap-4 bg-purple-950/40 border border-purple-700/50 rounded-lg p-4 hover:bg-purple-950/60 transition-colors"
        >
          <div className="flex items-center gap-3">
            <History className="h-5 w-5 text-purple-300" />
            <div>
              <div className="font-semibold text-white">Client Trade History</div>
              <div className="text-sm text-gray-400">Live client list and trade history</div>
            </div>
          </div>
          <span className="text-purple-300 text-sm">Open →</span>
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-6">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* System Health Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <div className="flex flex-row items-center justify-between space-y-0 pb-2">
            <h3 className="text-sm font-medium text-gray-400">System Status</h3>
            <Activity className="h-4 w-4 text-gray-500" />
          </div>
          <div className="mt-2">
            <div className="text-2xl font-bold text-white capitalize">
              {systemHealth?.status || 'Unknown'}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Firestore: {systemHealth?.firestore ? 'Connected' : 'Disconnected'}
            </p>
          </div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <div className="flex flex-row items-center justify-between space-y-0 pb-2">
            <h3 className="text-sm font-medium text-gray-400">Total Invocations (24h)</h3>
            <TrendingUp className="h-4 w-4 text-gray-500" />
          </div>
          <div className="mt-2">
            <div className="text-2xl font-bold text-white">
              {formatNumber(usageStats?.totalInvocations)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Avg response: {systemHealth?.functions?.averageResponseTime || 0}ms
            </p>
          </div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <div className="flex flex-row items-center justify-between space-y-0 pb-2">
            <h3 className="text-sm font-medium text-gray-400">Active Users</h3>
            <Users className="h-4 w-4 text-gray-500" />
          </div>
          <div className="mt-2">
            <div className="text-2xl font-bold text-white">
              {formatNumber(usageStats?.uniqueUsers)}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Last 24 hours
            </p>
          </div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <div className="flex flex-row items-center justify-between space-y-0 pb-2">
            <h3 className="text-sm font-medium text-gray-400">Error Rate</h3>
            <AlertCircle className="h-4 w-4 text-gray-500" />
          </div>
          <div className="mt-2">
            <div className="text-2xl font-bold text-white">
              {usageStats?.totalErrors || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {((systemHealth?.errors?.errorRate || 0) * 100).toFixed(2)}% error rate
            </p>
          </div>
        </div>
      </div>

      {/* Broker Integration Health */}
      {systemHealth?.brokers && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Broker Integration Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(systemHealth.brokers).map(([broker, data]) => (
              <div key={broker} className="border border-gray-600 rounded p-4 bg-gray-900">
                <h4 className="font-semibold capitalize text-gray-300 mb-2">{broker}</h4>
                <p className="text-sm text-gray-400">
                  Total Integrations: {data.totalIntegrations || 0}
                </p>
                <p className="text-sm text-gray-400">
                  Active: {data.activeIntegrations || 0}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Functions */}
      {usageStats?.topFunctions && usageStats.topFunctions.length > 0 && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Top Functions (24h)</h2>
          <div className="space-y-2">
            {usageStats.topFunctions.map((func, index) => (
              <div key={index} className="flex justify-between items-center">
                <span className="text-sm text-gray-300">{func.name}</span>
                <span className="text-sm font-semibold text-gray-400">{func.count} calls</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Logs */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-white mb-4">Recent System Logs</h2>
        {recentLogs.length === 0 ? (
          <p className="text-gray-500">No recent logs available</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {recentLogs.map((log, index) => (
              <div 
                key={log.id || index} 
                className={`p-3 rounded border ${
                  log.level === 'ERROR' ? 'bg-red-900/20 border-red-700' :
                  log.level === 'WARNING' ? 'bg-yellow-900/20 border-yellow-700' :
                  'bg-gray-900 border-gray-700'
                }`}
              >
                <div className="flex justify-between items-start mb-1">
                  <span className={`text-xs font-semibold ${
                    log.level === 'ERROR' ? 'text-red-400' :
                    log.level === 'WARNING' ? 'text-yellow-400' :
                    'text-gray-400'
                  }`}>
                    {log.level}
                  </span>
                  <span className="text-xs text-gray-500">
                    {formatTimestamp(log.timestamp)}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-300">{log.functionName}</p>
                <p className="text-sm text-gray-400">{log.message}</p>
                {log.error && (
                  <p className="text-xs text-red-400 mt-1">
                    {log.error.message}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;